import type { Offer } from 'types/seller'
import type { ChangeEvent } from 'react'

import { Fragment, useState } from 'react'

import { useStore } from 'store'
import { formatDate, formatZipCode, toCurrency } from 'utils'
import copy from 'copy.json'

import Button from 'components/Button'
import Status from 'components/Status'
import Checkbox from 'components/Checkbox'
import DetailsWrapper from 'components/layout/DetailsWrapper'
import Detail from 'components/layout/Detail'

type Props = {
  offer: Offer
  onChosenOffer: (offerId: number, checked: boolean) => void
  isOfferChosen: boolean
}

export default function OfferDetailsRow({ offer, onChosenOffer, isOfferChosen, ...props }: Props) {
  const { api } = useStore()
  const [expandDetails, setExpandDetails] = useState(false)
  const [offerDetails, setOfferDetails] = useState<Offer | null>(null)

  const handleDemandDetailsClick = async (offerId: number) => {
    if (!expandDetails) {
      const response = await api.getResource('offers', offerId)
      setOfferDetails(response)
    }

    setExpandDetails(current => !current)
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked
    onChosenOffer(offer.id, checked)
  }

  return (
    <Fragment {...props}>
      <tr
        className={`
        ${expandDetails ? 'expandedRow firstExpandedRow' : ''}
        ${isOfferChosen ? 'chosenOffer' : ''}`}
      >
        <td>
          <Checkbox checked={isOfferChosen} onChange={handleOnChange} />
        </td>

        <td>
          <Status statusType={offer.status} />
        </td>

        <td>
          <strong>{offer.id}</strong>
        </td>

        <td>
          <strong>{offer.energy_type}</strong>
        </td>

        <td>
          <strong>{offer.osd_name}</strong>
        </td>

        <td>{offer.electric_energy_meter_number}</td>
        <td>
          {offer.installation_power.toFixed(2).replace('.00', '')} {copy.common.MW}
        </td>
        <td>
          {offer.yearly_power_production
            ? offer.yearly_power_production + ' ' + copy.common.MWh
            : '-'}
        </td>
        <td>{toCurrency(offer.energy_price)}</td>

        <td>
          <Button
            secondary={expandDetails}
            small
            style={{ minWidth: 85, marginLeft: 'auto' }}
            onClick={() => handleDemandDetailsClick(offer.id)}
          >
            {expandDetails ? copy.common.button.roll : copy.common.button.unroll}
          </Button>
        </td>
      </tr>

      {offerDetails && (
        <tr className={expandDetails ? 'expandedRow' : 'hiddenRow'}>
          <td colSpan={10}>
            <DetailsWrapper>
              <DetailsWrapper>
                <Detail>
                  <span>{copy.common.form.installation_location}</span>
                  <span>
                    {formatZipCode(offerDetails.location_zip_code)} {offerDetails.location_city}
                  </span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.energy_point_number}</span>
                  <span>{offerDetails.energy_point_number || '-'}</span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.building_permit_number}</span>
                  <span>{offerDetails.building_permit_number || '-'}</span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.energy_multiplier}</span>
                  <span>{`x${offerDetails.energy_multiplier}`}</span>
                </Detail>
              </DetailsWrapper>

              <DetailsWrapper>
                <Detail>
                  <span>{copy.common.form.production_start_date}</span>
                  <span>
                    {formatDate(offerDetails.production_start_date, {
                      options: {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric'
                      }
                    })}
                  </span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.contract_length}</span>
                  <span>
                    {copy.common.from} {offerDetails.minimal_contract_length}{' '}
                    {copy.common.months_abbr} {copy.common.to}{' '}
                    {offerDetails.maximal_contract_length} {copy.common.months_abbr}
                  </span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.created_date}</span>
                  <span>{formatDate(offerDetails.created_date)}</span>
                </Detail>

                <Detail>
                  <span>{copy.common.form.last_modified_date}</span>
                  <span>{formatDate(offerDetails.last_modified_date)}</span>
                </Detail>
              </DetailsWrapper>
            </DetailsWrapper>
          </td>
        </tr>
      )}
    </Fragment>
  )
}
